import { MenuLayout } from '@templates/Menu/MenuLayout.ui';
import { GetServerSideProps } from 'next';
import { QueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';
import { createMerchantPath } from '@domain/merchants/hooks/useMerchantPath';
import { getMerchantThemeSSR } from '@domain/merchants/functions/getMerchantThemeSSR';
import { getFeatureFlags, ldCookieOptions } from '@olo-web/services/launchDarkly';
import nookies from 'nookies';
import { ECookies, EFirestoreCollections } from '@olo-web/types/enums';
import {
  configureMerchantData,
  configureMerchantGroupData,
} from '@domain/merchants/functions/configureMerchantData';
import { v4 as uuid } from 'uuid';
import { getLDUserValuesFromCtx } from '@olo-web/utils';
import { getMerchant } from '@olo-web/services/emagine/getMerchant';
import { getMerchantGroup } from '@olo-web/services/emagine/getMerchantGroup';
import { getMenu } from '@olo-web/services/emagine/getMenu';
import { roundDownDateTime } from '@olo-web/domain/orders/hooks/useRoundedDownOrderDateTime';
import { getMerchantIdentification } from '@olo-web/services/theseus/getMerchantIdentification';
import { getBusinessLocation } from '@olo-web/services/digitalOrderingService/getBusinessLocation';
import { EAIRecommendationVariations } from '@olo-web/types/enums/GoogleAISuggestionsVariations.enum';
import { getRecommendedItems } from '@olo-web/services/recommendations/getRecommendedItems';
import { validateBlid } from '@olo-web/utils/common/functions/validateBlid';
import { setGoogleConversionCookie } from '@olo-web/utils/common/functions/setGoogleConversionCookie';
import { generateImageUrls } from '@olo-web/utils/common/functions/generateImageUrls';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  try {
    const {
      res: { statusCode },
    } = ctx;
    const { merchantId: merchantIdFromSlug, ip } = getLDUserValuesFromCtx(ctx);
    // Extract rwg_token from query params and set to cookies
    setGoogleConversionCookie(ctx, merchantIdFromSlug as string);

    if (!merchantIdFromSlug) return { notFound: true };
    const cookie = nookies.get(ctx);
    const userKey = cookie[ECookies.ldUserKey] || uuid();
    if (!cookie[ECookies.ldUserKey]) nookies.set(ctx, ECookies.ldUserKey, userKey, ldCookieOptions);
    const customerId = cookie[ECookies.userId] || Math.floor(Math.random() * 1000000).toString();
    if (!cookie[ECookies.userId]) nookies.set(ctx, ECookies.userId, customerId, ldCookieOptions);
    const [
      featureFlags,
      merchantRes,
      merchantGroupRes,
      activeBlidSearchRes,
      merchantTheme,
      locationAppEnabledStatus,
    ] = await Promise.all([
      getFeatureFlags(userKey, merchantIdFromSlug as string, ip as string),
      getMerchant(merchantIdFromSlug),
      getMerchantGroup(merchantIdFromSlug as string),
      getMerchantIdentification({ id: merchantIdFromSlug as string }), // Adding this to support the recommendations feature which only works with merchantId
      getMerchantThemeSSR(merchantIdFromSlug),
      getBusinessLocation(merchantIdFromSlug as string),
    ]);

    const merchant = configureMerchantData(
      merchantRes?.data,
      featureFlags?.disallowDoorDashDriveOrders
    );
    const merchantGroup = configureMerchantGroupData(
      merchantGroupRes?.data,
      featureFlags?.disallowDoorDashDriveOrders
    );

    // Fetch the merchant and if ordering is unavailable redirect to ordering-unavailable
    if (!merchant?.isOrderingAvailable || statusCode === 500) {
      return {
        redirect: {
          destination: `${createMerchantPath({
            merchantId: merchantIdFromSlug as string,
            merchant,
            groupId: merchantGroup?.id,
          })}/ordering-unavailable`,
          permanent: false,
        },
      };
    }

    const queryClient = new QueryClient();

    const promises = [
      queryClient.prefetchQuery(
        ['merchant', merchantIdFromSlug],
        // @ts-ignore
        () => merchant
      ),
      queryClient.prefetchQuery(
        ['merchant-group', merchantIdFromSlug],
        // @ts-ignore
        () => merchantGroup
      ),
      queryClient.prefetchQuery(
        ['feature-flags', userKey],
        //@ts-ignore
        () => featureFlags
      ),
      queryClient.prefetchQuery(
        ['merchant-identification', merchantIdFromSlug],
        //@ts-ignore
        () => activeBlidSearchRes.data
      ),
      queryClient.prefetchQuery(
        ['location-app-enabled-status', activeBlidSearchRes?.data?.business_location_id],
        //@ts-ignore
        () => locationAppEnabledStatus
      ),
    ];

    //Prioritize the merchant id from the blid search if it exists
    const merchantIdForRecommendations =
      activeBlidSearchRes?.data?.merchant_id ||
      (validateBlid(merchantIdFromSlug) ? '' : merchantIdFromSlug);

    if (merchantIdForRecommendations) {
      const recommendationsEnabled = merchantTheme?.settings?.areRecommendationsEnabled ?? false;

      const recommendationsFeatureFlagEnabled =
        recommendationsEnabled &&
        (featureFlags?.GoogleAISuggestions === EAIRecommendationVariations.ALL ||
          featureFlags?.GoogleAISuggestions === EAIRecommendationVariations.MENU);

      if (recommendationsFeatureFlagEnabled) {
        promises.push(
          queryClient.prefetchQuery(['item-recommendations-for-you', customerId], async () => {
            const { data } = await getRecommendedItems({
              user_id: customerId,
              merchant_id: merchantIdForRecommendations,
              limit: 12,
            });
            return data?.recommendations;
          })
        );
      }
    }

    if (!featureFlags?.hideMerchantTheming) {
      promises.push(
        queryClient.prefetchQuery(
          [EFirestoreCollections.MERCHANT_THEME, merchantIdFromSlug],
          // @ts-ignore
          () => merchantTheme
        )
      );
    }

    if (merchant?.defaultOrderTypeId) {
      const roundedDownOrderDateTime = roundDownDateTime(
        merchant?.defaultOrderType?.asapOrderDateTime
      );

      promises.push(
        queryClient.prefetchQuery(
          ['menu', merchant?.merchantId, merchant?.defaultOrderTypeId, roundedDownOrderDateTime],
          async () => {
            const { data } = await getMenu(
              merchant?.merchantId,
              merchant?.defaultOrderTypeId,
              merchant?.defaultOrderType?.asapOrderDateTime,
              false
            );
            return data;
          }
        )
      );
    }

    await Promise.all(promises);

    // Extract menu data from React Query cache for SEO
    let menuData = null;
    if (merchant?.merchantId && merchant?.defaultOrderTypeId) {
      const roundedDownOrderDateTime = roundDownDateTime(
        merchant?.defaultOrderType?.asapOrderDateTime
      );
      try {
        menuData = queryClient.getQueryData([
          'menu',
          merchant.merchantId,
          merchant.defaultOrderTypeId,
          roundedDownOrderDateTime,
        ]);
      } catch (error) {
        console.error('Error getting menu data from cache:', error);
      }
    }

    // Generate image URLs for SEO component
    const imageUrls = generateImageUrls({
      merchantData: merchant,
      merchantGroupData: merchantGroup,
      merchantThemeData: merchantTheme,
    });

    return {
      props: {
        dehydratedState: dehydrate(queryClient),
        globalUIState: {
          ldUserKey: userKey,
          customerId: customerId,
        },
        // Pass merchant data for SEO component
        merchantData: merchant,
        merchantGroupData: merchantGroup,
        menuData: menuData,
        merchantId: merchantIdFromSlug,
        // Pass image URLs for SEO component
        ...imageUrls,
      },
      // every 6 hours (in secs)
    };
  } catch (e) {
    if (e?.response?.status === 404) {
      return {
        notFound: true,
      };
    }

    throw e;
  }
};

export default MenuLayout;
